import {  FormControl, ValidatorFn, Validators } from "@angular/forms";
import { ControlType } from "../../settings/model/control.model";
import { PhotoAttachment } from "src/app/model/file.model";
import { FormHeader } from '../../settings/model/form.model';
import { Task } from "./task.model";
import { DateISO, UserModel } from "../../../model/general.model";

const noArgValidators = {
  REQUIRED: Validators.required
} as const;

const oneArgValidators = {
  MAX: Validators.max,
  MIN: Validators.min,
  MAX_LENGTH: Validators.maxLength,
  MIN_LENGTH: Validators.minLength
} as const;

export type NoArgValidators = keyof typeof noArgValidators;
export type OneArgValidators = keyof typeof oneArgValidators;
export type DynamicValidators = NoArgValidators | OneArgValidators; //keyof (typeof noArgValidators & typeof oneArgValidators);

export const VALIDATORS: { [K in NoArgValidators]: ValidatorFn } & {[K in OneArgValidators]: ((arg: number) => ValidatorFn)} = {//{ [K in DynamicValidators]: ValidatorFn | ((arg: number) => ValidatorFn) } = {
  ...noArgValidators,
  ...oneArgValidators
};

export const VALIDATION_ERROR_MESSAGES: { [K in DynamicValidators]: string} = {
  'REQUIRED': 'ERRORS.VALIDATIONS.REQUIRED',
  'MIN': 'ERRORS.VALIDATIONS.MIN',
  'MAX': 'ERRORS.VALIDATIONS.MAX',
  'MAX_LENGTH': 'ERRORS.VALIDATIONS.MAX_LENGTH',
  'MIN_LENGTH': 'ERRORS.VALIDATIONS.MIN_LENGTH'
} as const;

export type ValidationErrorMessages = keyof typeof VALIDATION_ERROR_MESSAGES;

type BaseValidatorDef<T extends DynamicValidators> = {
  type: T;
};
export type NoArgValidatorDef = BaseValidatorDef<NoArgValidators>;

export type OneArgValidatorDef = BaseValidatorDef<OneArgValidators> & {
  argument: number;
};
export type ValidatorDef = NoArgValidatorDef | OneArgValidatorDef;

export interface Option {
  id?: number;
  label: string;
  value: string;
  isFailure: boolean;
  failureCode?: FailureCodeModel;
  defaultValue: boolean;
}

export type Control = {
  id: number;
  templateFormControlId: number;
  type: ControlType;
  label?: string;
  name: string;
  validators?: ValidatorDef[];
  options?: Option[];
  controls?: Omit<Control, 'controls'>[];
  value?: any;
  enableIfFailure: boolean;
  required: boolean;
}

export type FormEntry = {
  id: number;
  templateFormEntryId: number;
  cloneIndex?: number;
  name: string;
  displayName?: string;
  description?: string;
  showDescription: boolean;
  children?: FormEntry[];
  failureDescription?: FailureDescription[];
  failureMultiselect: boolean;
  cableSection: boolean,
  controlValues: Control[];
  customLabel?: string;
  customValue?: string;
  cloneTaskName?: string | null;
} & ( //failureCodeList będzie wymagane razem z control i zabronione w przypadku gdy nie ma control
  { control?: never; failureCodeList?: never } |
  { control: Control[]; failureCodeList: FailureCodeModel[] }
);

export interface FailureCodeModel {
  code: string;
  freeText: string;
}


export type FormEntryValue = {
  id: number;
  cloneIndex: number;
  failureDescription?: FailureDescription[];
} & ({
  children: FormEntryValue[];
  values?: ControlValue [];
} | {
  children?: FormEntryValue[];
  values: ControlValue [];
})


export type ControlValue = {
  id: number;
  value: unknown;
}

export type FormValue = { formEntries: FormEntryValue[] };

export const DEFAULT_FORM_ENTRY_CLONE_INDEX = -1;

export const getFormEntryCloneIndex = (cloneIndex?: number | null): number =>
  cloneIndex ?? DEFAULT_FORM_ENTRY_CLONE_INDEX;

export const getFormEntryInstanceKey = (
  templateFormEntryId: number,
  cloneIndex?: number | null
): string => `${templateFormEntryId}:${getFormEntryCloneIndex(cloneIndex)}`;

export const getFormEntryControlName = (
  templateFormEntryId: number,
  cloneIndex?: number | null
): string => `_${getFormEntryInstanceKey(templateFormEntryId, cloneIndex)}`;

export const parseFormEntryInstanceKey = (
  rawKey: string
): Pick<FormEntryValue, 'id' | 'cloneIndex'> => {
  const [id, cloneIndex] = rawKey.split(':');

  return {
    id: Number(id),
    cloneIndex: cloneIndex === undefined ? DEFAULT_FORM_ENTRY_CLONE_INDEX : Number(cloneIndex),
  };
}

export const FAILURE_CATEGORY = {
  RED: "RED",
  YELLOW: "YELLOW",
  GREEN: "GREEN",
} as const;

export type FailureCategory = keyof typeof FAILURE_CATEGORY;

export const FAILURE_STATUS = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED'
}

export type FailureStatus = keyof typeof FAILURE_STATUS;

export type FailureDescriptionBase = {
  id?: number;
  name: string,
  description: string;
  photos: PhotoAttachment[],
  category: FailureCategory;
  comment?: string;
  failureCodes: FailureCodeModel[];
  longitude: number;
  latitude: number;
  createdOn: DateISO;
  createdBy?: UserModel;
  modifiedBy?: UserModel;
  modifiedOn?: DateISO;
  status: FailureStatus;
}

export interface SpanFailureDescription extends FailureDescriptionBase{
  externalIdStart: string;
  externalIdEnd: string;
}

export interface TowerFailureDescription extends FailureDescriptionBase {
  externalId: string;
}

export type FailureDescription = SpanFailureDescription | TowerFailureDescription;

export type ExternalId = string;

export interface ExternalIdStartEnd {
  externalIdStart: ExternalId;
  externalIdEnd: ExternalId;
}

export interface LatitudeLongitude {
  latitude: number;
  longitude: number;
}

interface FailuresRequest {
  fromDate: DateISO;
}

export interface SpanFailuresRequest extends FailuresRequest {
  locations: LatitudeLongitude[];
}

export interface TowerFailuresRequest extends FailuresRequest {
  locations: LatitudeLongitude[];
}

export type FailureDialogData = {
  failureDescription?: FailureDescription | Omit<FailureDescription, 'id'>;
  failureCodeList: FailureCodeModel[];
  disabled: boolean;
  failureMultiselect: boolean;
  taskName: string;
  defectsLastNo: number;
  taskId: number;
  longitude: number;
  latitude: number;
  taskVersion: number;
  headers: FormHeader[];
  entry: FormEntry;
  parentEntry?: FormEntry;
  cloneTaskName?: string | null;
  customLabel?: string;
  customValue?: string;
}

export type FailureForm = {
  name: FormControl<string>,
  description: FormControl<string>;
  photos: FormControl<any>,
  category: FormControl<FailureCategory | null>
  comment: FormControl<string | null>;
  failureCodes: FormControl<string | string[]>;
}

export interface FormControlPayload {
  name: string;
  label: string;
  enableIfFailure: boolean;
  options: FormControlOption[]
}

export interface FormControlOption {
  label: string;
  optionValue: string;
  isFailure: boolean;
}

export interface CopyFormDialogData {
  task: Task;
}
